import React, { useEffect } from 'react';
import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import DateFnsUtils from '@date-io/date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { DIVISIONS, PRIORITIES, TYPES_OF_PACKING } from '../utils/lists';
import { getOrderInfo, registerOrder, updateOrder } from '../../actions/orders';
import {
	capLetters,
	defaultContacts,
	isInDevEnv,
	defaultContactsDev,
	handleError,
	handleSuccess,
	whichDivison,
	whichPriority,
	whichTypeOfPacking,
	whichEnvURL,
} from '../../helpers/utils';
import { diffDays, formatDateMX } from '../../helpers/hours';
import { Stack } from '@mui/system';
import { useNavigate, useParams } from 'react-router';
import { sendWhatsApp, template3 } from '../../helpers/whatsappApi';
import { getOrderContacts } from '../../actions/contact';
import { createWorkspace } from '../../actions/files';

export const QuotationForm = ({ edit = false }) => {
	// URL params
	const { idQuotation = null } = useParams();

	// Define state to know if it's loading or not
	const [isLoading, setIsLoading] = useState(false);

	// Form
	const [division, setDivision] = useState(null);
	const [client, setClient] = useState(null);
	const [clientObj, setClientObj] = useState(null);
	const [entryDate, setEntryDate] = useState(null);
	const [deliveryTime, setDeliveryTime] = useState('30');
	const [priority, setPriority] = useState(null);
	const [orderType, setOrderType] = useState(null);
	const [typeOfPacking, setTypeOfPacking] = useState(null);
	const [amount, setAmount] = useState('');
	const [notes, setNotes] = useState('');
	const [transport, setTransport] = useState('');
	const [consignedTo, setConsignedTo] = useState('');
	const [contactsList, setContactsList] = useState([]);

	// Dates
	const [deliveryDate, setDeliveryDate] = useState('');
	const [deliveryForecast, setDeliveryForecast] = useState('');

	// Current user
	const { user, clients } = useSelector((state) => state);

	// Navigation
	const navigate = useNavigate();

	// Path
	const path = `/dashboard/ordenes`;

	const handleContactListWhatsapp = async (order) => {
		// Get order contacts from DB
		const response = (await getOrderContacts(order?.id)).data;

		if (whichEnvURL(true) !== 'local') {
			// Create workspace in Firebase
			createWorkspace(order, user);
		}

		const clientAndUser = `${client}, registrado por el operador ${getFullName(
			user
		)}`;
		// Send WhatsApp
		sendWhatsApp(
			order,
			user,
			response?.ok ? response?.contacts : [],
			template3(clientAndUser, order?.number_order)
		)
			.then(({ data }) => {
				if (data?.ok || data?.msg.includes('sent')) {
					handleSuccess('Mensaje enviado');
					localStorage.setItem('currentPathPilu', path);
					navigate(path);
				} else {
					handleError(
						data?.msg,
						'Ocurrió un error al intentar enviar el mensaje. Intenta más tarde'
					);
				}
			})
			.catch((error) => {
				handleError(
					error,
					'Ocurrió un error al intentar enviar el mensaje. Intenta más tarde'
				);
			});
	};

	const generateFormData = () => {
		const addedDate = moment(entryDate)
			.add(parseInt(deliveryTime), 'days')
			.format('YYYY-MM-DD');

		return {
			notes,
			transport,
			consigned_to: capLetters(consignedTo),
			amount: parseFloat(amount),
			delivery_date: edit ? deliveryDate : addedDate,
			client_id: edit
				? clientObj.id
				: clients.find((item) => item.company_name === client)?.id,
			division: DIVISIONS.find((item) => item.label === division).name,
			priority: PRIORITIES.find((item) => item.label === priority).name,
			type: orderType === 'Orden' ? 'ORD' : 'QUO',
			type_of_packing: TYPES_OF_PACKING.find(
				(item) => item.label === typeOfPacking
			).name,
			entry_date: moment(entryDate).format('YYYY-MM-DD'),
			delivery_forecast: edit ? deliveryForecast : addedDate,
			created_by: user?.id,
			contacts: edit
				? contactsList.map((item) => item?.id)
				: isInDevEnv()
				? defaultContactsDev
				: defaultContacts,
		};
	};

	// Go to path
	const goTo = (currentPath) => {
		localStorage.setItem('currentPathPilu', currentPath);
		navigate(currentPath);
	};

	// Handle submit
	const handleSubmit = () => {
		// Set loading state to true
		setIsLoading(true);

		// Generate payload
		const formData = generateFormData();

		if (edit) {
			updateOrder(idOrder, formData)
				.then(() => {
					// Feedback success
					handleSuccess('Pedido actualizado correctamente.');

					// Set new path
					const path2 = `/dashboard/ordenes${
						edit ? `/${idOrder}` : ''
					}`;

					// Go to orders table
					goTo(path2);
				})
				.catch((error) => {
					handleError(error, 'No se pudo actualizar el pedido.');
				});
		} else {
			// Status ICF: in configuration
			const initialStatus = 'ICF';
			registerOrder({ ...formData, status: initialStatus })
				.then(async ({ data: order }) => {
					// Feedback success
					handleSuccess('Pedido agregado correctamente.');

					// Handle alert whatsapp
					await handleContactListWhatsapp(order);

					// Go to orders table or quotation engine
					goTo(
						orderType === 'Pedido'
							? path
							: `/dashboard/cotizacion/${order.id}`
					);
				})
				.catch((error) => {
					handleError(error, 'No se pudo agregar el pedido.');
				});
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (edit) {
			getOrderInfo(idOrder).then(({ data }) => {
				setContactsList(data.contacts);
				const diffDaysOrder = diffDays(
					formatDateMX(data?.entry_date),
					formatDateMX(data?.delivery_date)
				);

				setDeliveryDate(data?.delivery_date);
				setDeliveryForecast(data?.delivery_forecast);

				setDivision(data ? whichDivison(data.division) : null);
				setClient(data ? data.client_id.company_name : null);
				setClientObj(data ? data.client_id : null);
				setEntryDate(
					data ? moment(data?.entry_date).format('MM/DD/YYYY') : null
				);
				setDeliveryTime(data ? diffDaysOrder : '30');
				setPriority(data ? whichPriority(data.priority) : null);
				setTypeOfPacking(
					data ? whichTypeOfPacking(data.type_of_packing) : null
				);
				setAmount(data ? data.amount : '');
				setNotes(data ? data.notes : '');
				setTransport(data ? data.transport : '');
				setConsignedTo(data ? data.consigned_to : '');
				setOrderType(
					data ? (data.type === 'QUO' ? 'Cotización' : 'Orden') : null
				);
			});
		}
	}, []);

	const TextFieldComponent = (params, label) => (
		<TextField
			fullWidth
			required
			margin='normal'
			{...params}
			label={label}
		/>
	);

	return (
		<div style={{ padding: '25px' }}>
			<LocalizationProvider
				dateAdapter={AdapterDateFns}
				adapterLocale={es}
				utils={DateFnsUtils}
			>
				<Stack
					direction='column'
					alignItems='flex-start'
					justifyContent='space-between'
					style={{
						border: '1px solid white',
						padding: '20px 20px 0 0',
						margin: '0 0 10px 0',
						width: '100%',
					}}
				>
					<Typography variant='h4' gutterBottom>
						Pedidos
					</Typography>
					<Typography variant='p' gutterBottom>
						Complete el formulario para{' '}
						{edit ? 'editar la' : 'crear una nueva'} orden
					</Typography>
				</Stack>
				<hr />
				<Grid container spacing={2} className='mt-2'>
					<Grid item xs={4}>
						<Autocomplete
							disabled={edit}
							required
							style={{ marginTop: '-17px' }}
							fullWidth
							value={client}
							onChange={(_, value) => {
								setClient(value);
							}}
							options={clients.map((item) => item.company_name)}
							renderInput={(params) =>
								TextFieldComponent(params, 'Cliente')
							}
						/>
					</Grid>

					<Grid item xs={4}>
						<TextField
							disabled={edit}
							required
							fullWidth
							type='number'
							label='Tiempo de entrega'
							value={deliveryTime}
							onChange={({ target }) =>
								setDeliveryTime(target.value)
							}
						/>
					</Grid>

					<Grid item xs={6}>
						<TextField
							style={{ margin: '5px 0 0 0' }}
							fullWidth
							margin='normal'
							placeholder='Notas extra'
							multiline
							rows={3}
							value={notes}
							onChange={({ target }) => {
								setNotes(target.value);
							}}
						/>
					</Grid>

					<Grid item xs={6}>
						<LoadingButton
							fullWidth
							size='large'
							color='error'
							onClick={() => {
								let path2 = '/dashboard/ordenes';
								if (edit) {
									path2 = `/dashboard/order/${idOrder}`;
								}
								localStorage.setItem('currentPathPilu', path2);
								navigate(path2);
							}}
							variant='contained'
							loading={false}
						>
							Cancelar
						</LoadingButton>
					</Grid>

					<Grid item xs={6}>
						<LoadingButton
							fullWidth
							size='large'
							type='submit'
							variant='contained'
							loading={isLoading}
							style={{ backgroundColor: '#143f6c' }}
							onClick={() => {
								// Set amount to 0
								setAmount(0);

								// Handle submit
								handleSubmit();
							}}
						>
							{edit ? 'Actualizar' : 'Guardar'}
						</LoadingButton>
					</Grid>
				</Grid>
			</LocalizationProvider>
		</div>
	);
};
